import { useGameContext } from "GameContext";
import React, { useRef, useEffect, useState } from "react";

import { numToLandType, actions } from "utils/game-information-constants"

import {
  type GameState,
  getCellFaunaCapacity,
  getGrazingValue,
  getCellActionCost,
  getEmbankmentCost,
  getNatureReserveCost,
  getDisembankmentCost,

  mapNumberToColor,
} from "utils/game_utils";


import {
  CLEARINGCOSTS,
  WATER_LEVEL_MANAGEMENT_SCHEMES,
  getWMRIndexFromArray,
} from "utils/simulator";

export type FullscreenTileCanvasProps = {
  showDucksOnly?: boolean;
  showAnimals?: boolean;
  rows?: number;
  cols?: number;
  tileSize?: number; // base tile size in pixels at scale = 1
  onTileClick?: (r: number, c: number) => void;
  onSelectedTiles?: (selectedTiles: Array<{ x: number; y: number }>) => void;
  initialScale?: number;
  
  gameState?: GameState;

  currentView?: string;
  currentYear?: number;
  currentWMRegime?: number;
  currentSeason?: number;
  proposedMuActions?: Record<string, any>;
  proposedMapActions?: Record<string, any>;
  proposedFaunaActions?: Record<string, any>;
  reservationActions?: Record<string, any>;
  selectedTool?: string;

  //Hacky way of making the map recenter the screen on button press (should forward ref in the future)
  recenterScreen?: boolean;
  showRight?: boolean;
  showAdmin?: boolean;
};

// FullscreenTileCanvas
// - Fullscreen canvas that shows a grid of tiles
// - Panning: middle-button drag OR right-button drag OR hold Space + left-button drag
// - Zoom: mouse wheel (centered on cursor). Works in Firefox and Chrome.
// - Hover/select: left-click to select a tile; move mouse to hover.

export default function FullscreenTileCanvas({
  showDucksOnly=false,
  showAnimals=false,
  rows=10,
  cols=10,
  tileSize = 64,
  onTileClick,
  onSelectedTiles,
  initialScale = 1,

  gameState,
  currentView="land",
  currentYear=0,
  currentSeason=1,
  proposedMuActions={},
  proposedMapActions={},
  proposedFaunaActions={},
  reservationActions={},
  showRight=false,
  selectedTool="",

  recenterScreen=false,
  currentWMRegime=-1,

  showAdmin=false,
}: FullscreenTileCanvasProps) {

  const {
    playerInfo,
    currentGameState,
  } = useGameContext() as any;
  const activeGameState = gameState ?? currentGameState;
  const cellData = (activeGameState?.cellData ?? []) as any[];

  const actionsWithImages = [
    "clearing",
    "cut",
    "grazing",
    "private-hunt",
    "public-hunt",
  ]
  const imagesRef = useRef<Record<string, HTMLImageElement | null>>({
    "clearing": null,
    "cut": null,
    "grazing": null,
    "private-hunt": null,
    "public-hunt": null,

    "duck": null,
    "bittern" : null,
    "heron": null,
  });
  const [imagesLoaded, setImageLoaded] = useState(false);
  const loadImage = (key: string, src: string) =>
    new Promise<void>((resolve) => {
      const img = new Image();
      img.src = src;
      img.onload = () => {
        imagesRef.current[key] = img;
        resolve();
      };
    });

  // load images once
  useEffect(() => {
    Promise.all([
      loadImage("clearing", "/ActionIcons/clearing.png"),
      loadImage("cut", "/ActionIcons/Cut-Reed.png"),
      loadImage("grazing", "/ActionIcons/Graze.png"),
      loadImage("private-hunt", "/ActionIcons/Hunt.png"),
      loadImage("public-hunt", "/ActionIcons/Hunt.png"),


      loadImage("duck", "/ActionIcons/duck.png"),
      loadImage("bittern" , "/ActionIcons/bittern.png"),
      loadImage("heron", "/ActionIcons/Heron.png"),
    ]).then(() => {
      setImageLoaded(true);
    });
  }, []);


  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const rafRef = useRef<number | null>(null);

  const txRef = useRef(0);
  const tyRef = useRef(0);
  const scaleRef = useRef(initialScale);

  const draggingRef = useRef(false);
  const dragStartRef = useRef<{ x: number; y: number } | null>(null);
  const lastPointerRef = useRef<{ x: number; y: number } | null>(null);
  const pointerButtonRef = useRef<number | null>(null);

  const [selectedTiles, setSelectedTiles] = useState<Set<string>>(new Set());
  const [hoveredTile, setHoveredTile] = useState({ x: -1, y: -1});

  const paintingRef = useRef(false);
  const tileKey = (x: number, y: number) => `${x},${y}`;


  // Resize canvas relative to parent container
  const resizeCanvas = () => {
    const canvas = canvasRef.current!;
    const container = containerRef.current!;
    if (!canvas || !container) return;

    const rect = container.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    canvas.style.width = rect.width + "px";
    canvas.style.height = rect.height + "px";
    canvas.width = Math.max(1, Math.floor(rect.width * dpr));
    canvas.height = Math.max(1, Math.floor(rect.height * dpr));

    const ctx = canvas.getContext("2d");
    if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  };

  useEffect(() => {
    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);
    return () => window.removeEventListener("resize", resizeCanvas);
  }, []);

  // Helpers: screen <-> world
  const screenToWorld = (sx: number, sy: number) => {
    return {
      x: (sx - txRef.current) / scaleRef.current,
      y: (sy - tyRef.current) / scaleRef.current,
    };
  };
  const worldToScreen = (wx: number, wy: number) => {
    return {
      x: wx * scaleRef.current + txRef.current,
      y: wy * scaleRef.current + tyRef.current,
    };
  };

  // Zooming (centered at mouse)
  const zoomAt = (clientX: number, clientY: number, zoomFactor: number) => {
    const prevScale = scaleRef.current;
    const newScale = Math.max(0.1, Math.min(8, prevScale * zoomFactor));
    // clamp
    if (Math.abs(newScale - prevScale) < 1e-6) return;

    // world coordinate under cursor before and after
    const worldBefore = screenToWorld(clientX, clientY);

    scaleRef.current = newScale;

    const worldAfter = screenToWorld(clientX, clientY);

    // adjust translation so the world point under cursor stays in place
    txRef.current += (worldAfter.x - worldBefore.x) * scaleRef.current;
    tyRef.current += (worldAfter.y - worldBefore.y) * scaleRef.current;
  };

  // Pointer Events
  useEffect(() => {
    const canvas = canvasRef.current!;
    if (!canvas) return;

    const onPointerDown = (e: PointerEvent) => {
      canvas.setPointerCapture(e.pointerId);
      lastPointerRef.current = { x: e.clientX, y: e.clientY };
      pointerButtonRef.current = e.button;

      // Determine when dragging should start: middle/right OR (left + spacebar)
      const spaceHeld = (window as any).navigator && (window as any).navigator.userAgent.includes("Firefox") ? e.shiftKey || e.altKey : e.shiftKey || e.altKey;
      // We allow middle (1) or right (2) to pan, and left (0) + Space to pan
      const wantPan = e.button === 1 || e.button === 2 || (e.button === 0 && (e as any).buttons === 1 && (e as any).shiftKey);

      // Note: Using shiftKey for panning by default because Space can affect page scrolling in some browsers.
      // If you prefer Space to pan, change condition above to check e.code === 'Space' or e.key state.

      if (wantPan) {
        draggingRef.current = true;
        dragStartRef.current = { x: e.clientX, y: e.clientY };
      } else {
        const rect = canvas.getBoundingClientRect();
        const cx = e.clientX - rect.left;
        const cy = e.clientY - rect.top;
        const world = screenToWorld(cx, cy);
        const r = Math.floor(world.y / tileSize);
        let c = Math.floor(world.x / tileSize);

        if (r >= 0 && r < rows && c >= 0 && c < cols) {
          const cell = getCellFromGridRC(c,r)
          const key = tileKey(cell.x,cell.y)
          setSelectedTiles(prev => new Set(prev).add(key));
          paintingRef.current = true;

          onTileClick && onTileClick(cell.x, cell.y);
        }
      }
    };

    const onPointerMove = (e: PointerEvent) => {
      const canvasRect = canvas.getBoundingClientRect();
      const cx = e.clientX - canvasRect.left;
      const cy = e.clientY - canvasRect.top;

      // handle pan
      if (draggingRef.current && lastPointerRef.current) {
        const dx = e.clientX - lastPointerRef.current.x;
        const dy = e.clientY - lastPointerRef.current.y;
        txRef.current += dx;
        tyRef.current += dy;
        lastPointerRef.current = { x: e.clientX, y: e.clientY };
      } else {
        const world = screenToWorld(cx, cy);
        const r = Math.floor(world.y / tileSize);
        let c = Math.floor(world.x / tileSize);

        if (r >= 0 && r < rows && c >= 0 && c < cols) {
          const cell = getCellFromGridRC(c,r)
          if (paintingRef.current) {
            const key = tileKey(cell.x,cell.y)
            setSelectedTiles(prev => new Set(prev).add(key));
          }

          setHoveredTile({x: cell.x, y: cell.y})
        } else {
          setHoveredTile({x: -1, y: -1})
        }
      }
    };

    const onPointerUp = (e: PointerEvent) => {
      canvas.releasePointerCapture(e.pointerId);

      setSelectedTiles(new Set());
      paintingRef.current = false;

      draggingRef.current = false;
      dragStartRef.current = null;
      lastPointerRef.current = null;
      pointerButtonRef.current = null;
    };

    const onWheel = (e: WheelEvent) => {
      // prefer zoom on wheel. Prevent page scroll.
      e.preventDefault();

      // Normalize delta for Firefox and Chromium
      const delta = e.deltaY;
      // use an exponential zoom factor
      const zoomFactor = Math.exp(-delta * 0.0015); // tweak sensitivity
      zoomAt(e.clientX, e.clientY, zoomFactor);
    };

    canvas.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    canvas.addEventListener("wheel", onWheel, { passive: false });

    return () => {
      canvas.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      canvas.removeEventListener("wheel", onWheel);
    };
  }, [rows, cols, tileSize, onTileClick, showRight]);

  useEffect(() => {
    if (onSelectedTiles) {
      const res: Array<{ x: number; y: number }> = []
      for (const value of selectedTiles) {
        const [x, y] = value.split(",").map(Number);
        res.push({
          x, y
        })
      }
      onSelectedTiles(res)
    }
  }, [selectedTiles])


  function getCellFromGridRC(c: number, r: number) {
    if (!cellData.length || !Array.isArray(cellData[0])) {
      return null;
    }
    if (showRight && cols < rows) {
      const invertRows = cellData[0].length - 1
      if (cellData[c + 5] && cellData[c + 5][invertRows - r]) {
        return cellData[c + 5][invertRows - r]
      }
      
      return null
    }
    const invertRows = cellData[0].length - 1
    if (cellData[c] && cellData[c][invertRows - r]) {
      return cellData[c][invertRows - r]
    }
    return null
  }

  // rendering
  useEffect(() => {
    const canvas = canvasRef.current!;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const render = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      ctx.clearRect(0, 0, w, h);

      ctx.save();
      ctx.translate(txRef.current, tyRef.current);
      ctx.scale(scaleRef.current, scaleRef.current);

      // compute visible tiles
      const invScale = 1 / scaleRef.current;
      const left = -txRef.current * invScale;
      const top = -tyRef.current * invScale;
      const right = left + w * invScale;
      const bottom = top + h * invScale;

      const c0 = Math.max(0, Math.floor(left / tileSize));
      const c1 = Math.min(cols - 1, Math.floor(right / tileSize));
      const r0 = Math.max(0, Math.floor(top / tileSize));
      const r1 = Math.min(rows - 1, Math.floor(bottom / tileSize));

      const invertRows = cellData[0].length - 1

      const muus: number[] = []

      // Fill tiles (demo: checker pattern)
      for (let r = r0; r <= r1; r++) {
        for (let c = c0; c <= c1; c++) {
          const x = c * tileSize;
          const y = r * tileSize;
          const isAlt = (r + c) % 2 === 0;

          const cell = getCellFromGridRC(c,r);
          const landType = numToLandType(cell.landType);
          switch(currentView) {
            case "topo":
              ctx.fillStyle = mapNumberToColor(
                cell.elevation,
                -40,
                40,
                // '#a1d372',
                // '#ffa867',
                // '#fff878',
                "#453e61",
                '#ffa867',
              )
              break;
            case "water":
              let useYear = Math.max(currentYear - 1, 1);
              let yearLog = cell.wlLog.slice( (useYear - 1) * 4,  (useYear - 1) * 4 + 4) 

              let seasonLog = 0;
              for (let k=0 ; k < yearLog.length; k++) {
                if (yearLog[k][1] === currentSeason - 1) {
                  seasonLog = yearLog[k][0];
                  break;
                }
              }
              ctx.fillStyle = mapNumberToColor(
                seasonLog,
                0,
                40,
                '#62f0ed',
                '#081a63',
                '#C79428',
              );
              break;
            default:
              ctx.fillStyle = landType.color; //isAlt ? "#e7f6ff" : "#f7fbff";
          }

          ctx.fillRect(x, y, tileSize, tileSize);

          if ((showAnimals || showDucksOnly) && activeGameState) {
            // currentWMRegime
            const wmwm = currentWMRegime > -1 ? currentWMRegime - 1 : 2
            const currentWM = WATER_LEVEL_MANAGEMENT_SCHEMES[wmwm]

            const duckCapacity = getCellFaunaCapacity(cell.x, cell.y, activeGameState, currentWM, "DUCK");
            const bitternCapacity = getCellFaunaCapacity(cell.x, cell.y, activeGameState, currentWM, "BOOMING_BITTERN");
            const heronCapacity = getCellFaunaCapacity(cell.x, cell.y, activeGameState, currentWM, "PURPLE_HERON_COLONY");

            let faunaImageCount = 0
            if (duckCapacity > 3) {
              if (imagesRef.current["duck"]) {
                ctx.drawImage(
                  imagesRef.current["duck"]!,
                  x + 4 + (tileSize*faunaImageCount/4),
                  y + tileSize*3/4,
                  tileSize / 4,
                  tileSize / 4,
                );
                faunaImageCount += 1
              }
            } 

            if (!showDucksOnly) {
              if (bitternCapacity >= 0.3) {
                if (imagesRef.current["bittern"]) {
                  ctx.drawImage(
                    imagesRef.current["bittern"]!,
                    x + 4 + (tileSize*faunaImageCount/4),
                    y + tileSize*3/4,
                    tileSize / 4,
                    tileSize / 4,
                  );
                  faunaImageCount += 1
                }
              } 
              if (heronCapacity > 4) {
                if (imagesRef.current["heron"]) {
                  ctx.drawImage(
                    imagesRef.current["heron"]!,
                    x + 4 + (tileSize*faunaImageCount/4),
                    y + tileSize*3/4,
                    tileSize / 4,
                    tileSize / 4,
                  );
                  faunaImageCount += 1
                }
              }
            }
          }
          // draw tile content (example: tile coordinates)

          const hasSameMU = (xx: number, yy: number) => {
            if (cellData[xx] && cellData[xx][yy]) {
              return cellData[xx][yy]?.managementUnit === cell.managementUnit
            }
            return false
          };

          let drawMUHoverBorder = hasSameMU(hoveredTile.x, hoveredTile.y) && (selectedTool === "embank" || selectedTool === "disembank" || selectedTool === "create-nature-reserve");

          ctx.fillStyle = "#333";
          ctx.font = `${Math.max(10, tileSize * 0.12)}px sans-serif`;
          ctx.textBaseline = "top";

          let muCosts = 0;

          const isVisuallyDisembanked = (proposedMuActions[`${cell.managementUnit}`]?.actionKind === "disembank")

          const muEntry = activeGameState?.muData?.[cell.managementUnit];
          const isVisuallyEmbanked = (!!muEntry && !muEntry.embanked && (proposedMuActions[`${cell.managementUnit}`]?.actionKind === "embank")) || (
            !!muEntry && muEntry.embanked && (proposedMuActions[`${cell.managementUnit}`]?.actionKind !== "disembank")
          )

          let yOff = 0;
          const wmDucks: number[] = []
          switch (selectedTool) {
            case "clearing":
              const clearingCosts = CLEARINGCOSTS[cell.landType];
              if (clearingCosts < 0) {
                ctx.fillText(``, x + 4, y + 4);
              } else {
                ctx.fillText(`Cost: ${clearingCosts}`, x + 4, y + 4);
              }
              break;
            case "cut":
              const wmCosts: number[] = []
              ctx.font = `${Math.max(8, tileSize * 0.12)}px sans-serif`;
              const cutSimType = typeof actions["cut"]?.simType === "number" ? actions["cut"]!.simType : 0;

              for (let i=0; i < WATER_LEVEL_MANAGEMENT_SCHEMES.length; i++) {
                const cost = getCellActionCost(
                  cutSimType,
                  cell.x, cell.y,
                  activeGameState,
                  WATER_LEVEL_MANAGEMENT_SCHEMES[i],
                )

                if (typeof cost === "number") {
                  wmCosts.push(cost);
                }
              }

              for (let i=0; i < wmCosts.length; i++) {
                if (!wmCosts[i]) {
                  continue
                }
                const wmcost = -wmCosts[i]

                const sybole = wmcost <= 0 ? "-"
                  : wmcost <= 675 ? "+"
                  : wmcost <= 1350 ? "++"
                  : wmcost <= 1800 ? "+++" : ""

                // 675 LOW (reed water)
                // 1350 standard
                // 1800 high yield

                // ctx.fillText(`WM ${i+1}: ${wmcost < 0 ? "+" : (wmcost === 0 ? "" : "-") }${Math.abs(wmcost)}`, x + 4, y + 4 + 10*(i-yOff));
                ctx.fillText(`WM ${i+1}: ${sybole}`, x + 4, y + 4 + 10*(i-yOff));
              }
              break;
            case "embank":
              muCosts = getEmbankmentCost(cell.managementUnit, activeGameState);
              if (muCosts < 0) {
                ctx.fillText("", x,y);
                break;
              }

              if (!muus.includes(cell.managementUnit)) {
                muus.push(cell.managementUnit);
                ctx.fillText(`Unit ${cell.managementUnit}`, x+4,y+4);

                const muInfo = activeGameState?.muData?.[cell.managementUnit];
                if (muInfo && muInfo.embanked) {
                  const wmr: number[] = Array.isArray(muInfo.wmRegime) ? muInfo.wmRegime : [];
                  const fuwIndex = getWMRIndexFromArray(wmr);
                  const fuw = (fuwIndex ?? 0) + 1;
                  ctx.fillText(`Current ${fuw}`, x+4,y+4 + 10);
                } else if (proposedMuActions[`${cell.managementUnit}`]?.actionKind === "embank") {
                  const proposedRegime = proposedMuActions[`${cell.managementUnit}`].wmRegime ?? [];
                  const fuwIndex = getWMRIndexFromArray(proposedRegime as number[]);
                  const fuw = (fuwIndex ?? 0) + 1;
                  ctx.fillText(`Current ${fuw}`, x+4,y+4 + 10);
                } else if (muCosts > 0) {
                  ctx.fillText(`Cost: ${muCosts}`, x+4,y+4 + 10);
                }
              } else {
                ctx.fillText("", x,y);
              }

              break
            case "disembank":
              muCosts = getDisembankmentCost(cell.managementUnit, activeGameState);
              if (muCosts < 0) {
                ctx.fillText("", x,y);
                break;
              }

              if (!muus.includes(cell.managementUnit)) {
                muus.push(cell.managementUnit);
                ctx.fillText(`Unit ${cell.managementUnit}`, x+4,y+4);


                if (isVisuallyDisembanked) {
                  ctx.fillText(`Undo Dike`, x+4,y+4 + 10);
                } else if (muCosts > 0 && activeGameState?.muData?.[cell.managementUnit]?.embanked) {
                  ctx.fillText(`Cost: ${muCosts}`, x+4,y+4 + 10);
                }
              } else {
                ctx.fillText("", x,y);
              }
              break
            case "private-hunt":
              for (let i=0; i < WATER_LEVEL_MANAGEMENT_SCHEMES.length; i++) {
                const duckCapacity = getCellFaunaCapacity(cell.x, cell.y, activeGameState, WATER_LEVEL_MANAGEMENT_SCHEMES[i]); // TODO FAUNA NAME
                wmDucks.push(duckCapacity);
              }

              for (let i=0; i < wmDucks.length; i++) {
                if (!wmDucks[i]) {
                  yOff += 1
                  continue
                }
                const wmd = Math.abs(wmDucks[i]) 
                const symbol = wmd >= 16 ? "+++"
                  : wmd >= 8 ? "++"
                  : wmd >= 4 ? "+" : ""
                ctx.fillText(`WM ${i+1}: ${symbol}`, x + 4, y + 4 + 10*(i-yOff));
              }
              break;
            case "public-hunt":
              for (let i=0; i < WATER_LEVEL_MANAGEMENT_SCHEMES.length; i++) {
                const duckCapacity = getCellFaunaCapacity(cell.x, cell.y, activeGameState, WATER_LEVEL_MANAGEMENT_SCHEMES[i]); // TODO FAUNA NAME
                wmDucks.push(duckCapacity);
              }

              for (let i=0; i < wmDucks.length; i++) {
                if (!wmDucks[i]) {
                  yOff += 1
                  continue
                }
                const wmd = Math.abs(wmDucks[i]) 
                const symbol = wmd >= 16 ? "+++"
                  : wmd >= 8 ? "++"
                  : wmd >= 4 ? "+" : ""
                ctx.fillText(`WM ${i+1}: ${symbol}`, x + 4, y + 4 + 10*(i-yOff));
                // ctx.fillText(`WM ${i+1}: ${Math.abs(wmDucks[i])}`, x + 4, y + 4 + 10*(i-yOff));
              }
              break;

            case "grazing":
              const ggs: number[][] = []
              for (let i=0; i < WATER_LEVEL_MANAGEMENT_SCHEMES.length; i++) {
                const gValues = getGrazingValue(cell.x, cell.y, activeGameState, WATER_LEVEL_MANAGEMENT_SCHEMES[i])
                ggs.push(gValues);
              }
              for (let i=0; i < ggs.length; i++) {
                if (
                  ggs[i][0] === 0 &&
                  ggs[i][1] === 0 &&
                  ggs[i][2] === 0 &&
                  ggs[i][3] === 0
                ) {
                  yOff += 1
                  continue
                }
                // ctx.fillText(`${i+1}: ${ggs[i].toString()}`, x + 4, y + 4 + 10*(i-yOff));
                const summm = ggs[i].reduce((total, current) => {
                  return total + current;
                }, 0);
                const symboll = summm >= 18 ? "+++"
                  : summm >= 10 ? "++"
                  : summm > 0 ? "+"
                  : "-"
                ctx.fillText(`WM ${i+1}: ${symboll}`, x + 4, y + 4 + 10*(i-yOff));
              }
              break;
            case "create-nature-reserve":
              if (reservationActions[cell.managementUnit] || activeGameState?.muData?.[cell.managementUnit]?.natureReserve) {
                // already nature reserved
                break;
              }
              if (!muus.includes(cell.managementUnit)) {
                muus.push(cell.managementUnit);
                const costs = getNatureReserveCost(cell.managementUnit, activeGameState);
                if (costs > 0) {
                  ctx.fillText(`Cost: ${costs}`, x+4,y+4);
                }
              }
              break;
            default:
              if (!muus.includes(cell.managementUnit)) {
                muus.push(cell.managementUnit);
                ctx.fillText(`Unit ${cell.managementUnit}`, x+4,y+4);


                // TODO HANDLE DRAWING THE EMBANKMENT WHEN IT COMES FROM GAME STATE (I.E. NOT PROPOSAL)
                if (proposedMuActions[`${cell.managementUnit}`]?.actionKind === "embank") {
                  const proposedRegime = proposedMuActions[`${cell.managementUnit}`].wmRegime ?? [];
                  const fuwIndex = getWMRIndexFromArray(proposedRegime as number[]);
                  ctx.fillText(`WM: ${(fuwIndex ?? 0) + 1}`, x+4,y+4 + 10);
                } else if (muEntry && muEntry.embanked) {
                  if (proposedMuActions[`${cell.managementUnit}`]?.actionKind === "disembank") {
                    ctx.fillText(`Set to clear`, x+4,y+4 + 10);
                  } else {
                    const wmr: number[] = Array.isArray(muEntry.wmRegime) ? muEntry.wmRegime : [];
                    const fuwIndex = getWMRIndexFromArray(wmr);
                    ctx.fillText(`WM: ${(fuwIndex ?? 0) + 1}`, x+4,y+4 + 10);
                  }
                }
              } else {
                ctx.fillText("", x,y);
              }
              // ctx.fillText(`${cell.x},${cell.y}, ${landType.name}`, x + 4, y + 4);
          }

          // DRAW MU borders

          if (isVisuallyEmbanked || isVisuallyDisembanked) {
            if (proposedMuActions[`${cell.managementUnit}`]?.actionKind === "embank") {
              ctx.strokeStyle = "rgba(0, 0, 0, 0.5)";
              ctx.lineWidth = 10 / scaleRef.current;
            } else {
              ctx.strokeStyle = "rgba(0, 0, 0, 1)";
              ctx.lineWidth = 7 / scaleRef.current;
            }

            if (isVisuallyDisembanked) {
              ctx.setLineDash([5, 5]);
            }
          } else {
            ctx.strokeStyle = "rgba(0, 0, 0, 0.75)";
            ctx.lineWidth = 2 / scaleRef.current;
          }

          ctx.beginPath();

          // top edge
          if (!hasSameMU(cell.x, cell.y+1)) {
            ctx.moveTo(x, y);
            ctx.lineTo(x + tileSize, y);
          }
          // bottom edge
          if (!hasSameMU(cell.x, cell.y-1)) {
            ctx.moveTo(x, y + tileSize);
            ctx.lineTo(x + tileSize, y + tileSize);
          }
          // left edge
          if (!hasSameMU(cell.x-1, cell.y)) {
            ctx.moveTo(x, y);
            ctx.lineTo(x, y + tileSize);
          }
          // right edge
          if (!hasSameMU(cell.x+1, cell.y)) {
            ctx.moveTo(x + tileSize, y);
            ctx.lineTo(x + tileSize, y + tileSize);
          }

          ctx.stroke();
          ctx.setLineDash([]);

          if (drawMUHoverBorder && selectedTool !== "disembank") {
            ctx.strokeStyle = "rgba(1.0, 0.0, 0.0, 0.1)";
            ctx.lineWidth = 10 / scaleRef.current;

            ctx.beginPath();

            // top edge
            if (!hasSameMU(cell.x, cell.y+1)) {
              ctx.moveTo(x, y);
              ctx.lineTo(x + tileSize, y);
            }
            // bottom edge
            if (!hasSameMU(cell.x, cell.y-1)) {
              ctx.moveTo(x, y + tileSize);
              ctx.lineTo(x + tileSize, y + tileSize);
            }
            // left edge
            if (!hasSameMU(cell.x-1, cell.y)) {
              ctx.moveTo(x, y);
              ctx.lineTo(x, y + tileSize);
            }
            // right edge
            if (!hasSameMU(cell.x+1, cell.y)) {
              ctx.moveTo(x + tileSize, y);
              ctx.lineTo(x + tileSize, y + tileSize);
            }

            ctx.stroke();
            ctx.setLineDash([]);
          }

          // Draw nature reserves
          if (reservationActions[cell.managementUnit] || activeGameState?.muData?.[cell.managementUnit]?.natureReserve) {
            ctx.setLineDash([5, 5]);
            if (activeGameState?.muData?.[cell.managementUnit]?.natureReserve) {
              ctx.strokeStyle = "rgba(0.0, 128.0, 128.0, 1.0)";
            } else {
              ctx.strokeStyle = "rgba(0.0, 128.0, 128.0, 0.75)";
            }

            ctx.lineWidth = 10 / scaleRef.current;

            ctx.beginPath();

            // top edge
            if (!hasSameMU(cell.x, cell.y+1)) {
              ctx.moveTo(x, y);
              ctx.lineTo(x + tileSize, y);
            }
            // bottom edge
            if (!hasSameMU(cell.x, cell.y-1)) {
              ctx.moveTo(x, y + tileSize);
              ctx.lineTo(x + tileSize, y + tileSize);
            }
            // left edge
            if (!hasSameMU(cell.x-1, cell.y)) {
              ctx.moveTo(x, y);
              ctx.lineTo(x, y + tileSize);
            }
            // right edge
            if (!hasSameMU(cell.x+1, cell.y)) {
              ctx.moveTo(x + tileSize, y);
              ctx.lineTo(x + tileSize, y + tileSize);
            }

            ctx.stroke();
            ctx.setLineDash([]);
          }

          // Map action icon?
          const key = `${cell.x}-${cell.y}`;
          const mapAction = proposedMapActions[key];
          const faunaAction = proposedFaunaActions[key];

          let hasMapAction = false
          if (mapAction?.actionKind && actionsWithImages.includes(mapAction.actionKind)) {
            // TODO TODO TODO 
            if ((mapAction.authorRole === playerInfo?.role && playerInfo?.estate === mapAction.authorEstateClass) || showAdmin) {
              if (imagesRef.current[mapAction.actionKind]) {
                ctx.drawImage(
                  imagesRef.current[mapAction.actionKind]!,
                  x + tileSize/2,
                  y + tileSize/2,
                  tileSize / 2,
                  tileSize / 2,
                );
                hasMapAction=true
              }
            }
          }

          if (faunaAction) {
            if (imagesRef.current["public-hunt"]) {
              ctx.drawImage(
                imagesRef.current["public-hunt"]!,
                x + tileSize/2,
                y + tileSize/2 + (hasMapAction ? -tileSize/2 : 0),
                tileSize / 2,
                tileSize / 2,
              );
            }
          }
        }
      }

      // grid lines
      ctx.beginPath();
      for (let r = r0; r <= r1 + 1; r++) {
        const y = r * tileSize;
        ctx.moveTo(left - tileSize, y);
        ctx.lineTo(right + tileSize, y);
      }
      for (let c = c0; c <= c1 + 1; c++) {
        const x = c * tileSize;
        ctx.moveTo(x, top - tileSize);
        ctx.lineTo(x, bottom + tileSize);
      }
      ctx.lineWidth = Math.max(1 / scaleRef.current, 0.5);
      ctx.strokeStyle = "rgba(0,0,0,0.08)";
      ctx.stroke();

      ctx.restore();

      rafRef.current = requestAnimationFrame(render);
    };

    rafRef.current = requestAnimationFrame(render);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [
    playerInfo,
    rows, cols, tileSize,
    cellData, currentView, currentSeason, currentYear,
    selectedTiles,
    proposedMapActions,
    proposedMuActions,
    showRight, selectedTool, activeGameState, hoveredTile,
  ]);

  function centerGrid () {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();

    const worldW = cols * tileSize;
    const worldH = rows * tileSize;

    scaleRef.current = initialScale;

    txRef.current = (rect.width - worldW * scaleRef.current) / 2;
    tyRef.current = (rect.height - worldH * scaleRef.current) / 2;
  }

  // initial pan to center grid inside parent
  useEffect(() => {
    centerGrid();
  }, [cols, rows, tileSize, initialScale, recenterScreen]);

  return (
    <div
      ref={containerRef}
      style={{ display: "flex", width: "100%", height: "100%", position: "relative" }}
    >
      <canvas
        ref={canvasRef}
        style={{
          display: "block",
          width: "100%",
          height: "100%",
          touchAction: "none",
          cursor: draggingRef.current ? "grabbing" : "default",
        }}
        onContextMenu={(e) => e.preventDefault()}
      />
    </div>
  );
}
